import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useTitle, sq } from "@/lib/img";
import { MaskedLines, FadeUp } from "@/components/Reveal";

export default function Press() {
  useTitle(
    "Press | Medium Plenty",
    "Press coverage and recognition for Medium Plenty — AD Middle East, Elle Decor, Wallpaper*, Dwell, Dezeen, the Los Angeles Times, Monocle, and more."
  );
  const [items, setItems] = useState([]);
  const [shown, setShown] = useState(8);

  useEffect(() => {
    api.get("/press").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const visible = items.slice(0, shown);
  const remaining = items.length - visible.length;

  return (
    <div data-testid="press-page" className="pt-32 md:pt-44 pb-24 md:pb-36 px-6 md:px-12 min-h-screen">
      <h1 className="font-serif font-light text-[12vw] md:text-[7vw] leading-[0.95] tracking-tight mb-8">
        <MaskedLines lines={["Press"]} delay={0.15} />
      </h1>
      <FadeUp delay={0.2} className="mb-16 md:mb-24">
        <p className="font-serif font-light text-sm md:text-base leading-[1.4] tracking-tight max-w-2xl">
          “Word of mouth is the best medium of all.”
        </p>
        <p className="kicker mt-5">— William Bernbach</p>
      </FadeUp>

      <div>
        {visible.map((item, i) => {
          const inner = (
            <div className="flex items-center gap-6 md:gap-10">
              {item.image && (
                <span className="block w-20 sm:w-28 md:w-40 shrink-0 overflow-hidden bg-sand">
                  <img
                    src={sq(item.image, 500)}
                    alt=""
                    loading="lazy"
                    className="w-full aspect-square object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </span>
              )}
              <span className="flex-1 min-w-0">
                <span className="flex flex-col md:flex-row md:items-baseline justify-between gap-2 md:gap-8">
                  <span className="text-[11px] uppercase tracking-[0.28em] text-smoke shrink-0 md:w-44">
                    {item.publication}
                  </span>
                  <span className="font-serif font-light text-lg md:text-2xl tracking-tight flex-1 transition-transform duration-700 ease-out group-hover:translate-x-3">
                    {item.title}
                  </span>
                  {item.link ? (
                    <ArrowUpRight
                      size={22}
                      strokeWidth={1}
                      className="text-smoke shrink-0 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-ink"
                    />
                  ) : (
                    <span className="text-[11px] uppercase tracking-[0.25em] text-smoke/60 shrink-0">{item.date}</span>
                  )}
                </span>
              </span>
            </div>
          );
          return (
            <FadeUp key={item.id} delay={i * 0.06}>
              {item.link ? (
                <a
                  data-testid={`press-item-${item.id}`}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block border-t border-line py-8"
                >
                  {inner}
                </a>
              ) : (
                <div data-testid={`press-item-${item.id}`} className="group border-t border-line py-8">
                  {inner}
                </div>
              )}
            </FadeUp>
          );
        })}
      </div>

      {items.length === 0 && (
        <p className="text-smoke">Press coverage is being gathered — please check back soon.</p>
      )}

      {remaining > 0 && (
        <FadeUp className="mt-8 md:mt-12 text-center">
          <button
            data-testid="press-load-more-btn"
            onClick={() => setShown((s) => s + 12)}
            className="editorial-link font-serif italic text-xl md:text-2xl text-ink"
          >
            Load More — {remaining} further {remaining === 1 ? "mention" : "mentions"}
          </button>
        </FadeUp>
      )}
    </div>
  );
}
